/**
 * Document Review AI Functions
 * Uses Groq (Mixtral-8x7b) to analyze legal documents
 */

import { getCountryLegalContext } from "@/shared/config/country-legal-context";
import { getGroq, GROQ_MODELS } from "./groq-client";

function stripGeneratedNotes(content: string): string {
  return content
    .replace(
      /\n*\s*(NOTA|NOTE)\s*:\s*Este documento ha sido generado con asistencia de inteligencia artificial\.[\s\S]*$/i,
      "",
    )
    .trim();
}

export interface DocumentAnalysisParams {
  content: string;
  documentType: string;
  country: string;
  documentName?: string;
  language?: "en" | "es"; // User's preferred language for analysis
}

export interface RiskItem {
  severity: "low" | "medium" | "high" | "critical";
  description: string;
  location?: string;
  recommendation?: string;
}

export interface OmissionItem {
  description: string;
  recommendation: string;
  importance: "low" | "medium" | "high";
}

export interface SensitiveClause {
  clause: string;
  concern: string;
  suggestion: string;
  severity: "low" | "medium" | "high";
}

export interface DocumentAnalysisResult {
  overall_risk: "low" | "medium" | "high";
  risks: RiskItem[];
  omissions: OmissionItem[];
  sensitive_clauses: SensitiveClause[];
  recommendations: string[];
  summary: string;
  confidence_score?: number; // 0-100
}

/**
 * Analyze a legal document using Groq AI
 */
export async function analyzeDocumentWithGroq(
  params: DocumentAnalysisParams
): Promise<DocumentAnalysisResult> {
  const {
    content,
    documentType,
    country,
    documentName,
    language = "es",
  } = params;
  const groq = getGroq();

  // Build country-specific legal context
  const countryContext = getCountryLegalContext(country, language);

  // Language-specific prompts
  const prompts = {
    es: {
      system: `Eres un experto revisor legal sénior especializado en derecho latinoamericano y comercial internacional. Tu misión es realizar un análisis crítico e "inteligente" de documentos legales, actuando como un consultor legal preventivo.

Debes proporcionar un análisis exhaustivo que incluya:
1. RIESGOS LEGALES ESTRATÉGICOS: Identifica cláusulas abusivas, ambigüedades peligrosas, desequilibrios contractuales graves o términos que contravengan leyes imperativas.
2. OMISIONES CRÍTICAS: Detecta elementos esenciales que faltan según la naturaleza del documento y las leyes locales (ej. falta de cláusula penal, orfandad en la resolución de conflictos, ausencia de limitaciones de responsabilidad).
3. PUNTOS DE NEGOCIACIÓN: Señala cláusulas que, aunque legales, son desfavorables y podrían ser renegociadas para mejorar la posición del cliente.
4. RECOMENDACIONES TÉCNICAS: Proporciona redacciones alternativas o ajustes específicos basados en la jurisprudencia y doctrina de ${country}.
5. RESUMEN EJECUTIVO: Un análisis de alto nivel sobre la salud jurídica general del documento.

CONTEXTO LEGAL ESPECÍFICO APLICABLE:
${countryContext}

REGLAS DE ORO:
- NO INVENTES: Si no estás seguro de una ley específica de ${country}, cita el principio general de derecho aplicable.
- SÉ ESPECÍFICO: No digas "la cláusula es vaga", di "la cláusula X es vaga porque no define el término Y, lo que genera riesgo de Z".
- INTELIGENCIA: Ve más allá de lo obvio. Busca riesgos ocultos en la letra pequeña y en la interacción entre diferentes cláusulas.

FORMATO DE RESPUESTA:
Debes responder ÚNICAMENTE con un objeto JSON válido (sin markdown, sin backticks) con la siguiente estructura:
{
  "overall_risk": "low" | "medium" | "high",
  "risks": [
    {
      "severity": "low" | "medium" | "high" | "critical",
      "description": "Explicación técnica del riesgo",
      "location": "Referencia exacta a la cláusula/sección",
      "recommendation": "Acción correctiva inmediata"
    }
  ],
  "omissions": [
    {
      "description": "Elemento faltante y por qué es necesario",
      "recommendation": "Texto sugerido para incluir",
      "importance": "low" | "medium" | "high"
    }
  ],
  "sensitive_clauses": [
    {
      "clause": "Texto original",
      "concern": "Explicación de la sensibilidad jurídica",
      "suggestion": "Redacción alternativa sugerida",
      "severity": "low" | "medium" | "high"
    }
  ],
  "recommendations": ["Consejo estratégico 1", "Consejo estratégico 2", ...],
  "summary": "Análisis ejecutivo profesional (2-3 párrafos)",
  "confidence_score": 0-100
}

IMPORTANTE: El análisis debe ser de calidad "Socio de Firma de Abogados", no un simple resumen de texto.`,
      user: `Realiza un análisis profundo del siguiente documento:

TIPO: ${documentType}
PAÍS: ${country}
${documentName ? `ARCHIVO: ${documentName}` : ""}

CONTENIDO:
---
${content}
---

Genera el informe técnico legal en formato JSON.`,
    },
    en: {
      system: `You are a senior legal review expert specializing in Latin American and international commercial law. Your mission is to perform a critical and "intelligent" analysis of legal documents, acting as a preventive legal consultant.

You must provide a comprehensive analysis that includes:
1. STRATEGIC LEGAL RISKS: Identify abusive clauses, dangerous ambiguities, serious contractual imbalances, or terms that violate mandatory laws.
2. CRITICAL OMISSIONS: Detect essential elements that are missing according to the nature of the document and local laws (e.g., lack of penalty clause, conflict resolution gap, absence of liability limitations).
3. NEGOTIATION POINTS: Point out clauses that, while legal, are unfavorable and could be renegotiated to improve the client's position.
4. TECHNICAL RECOMMENDATIONS: Provide alternative wording or specific adjustments based on the jurisprudence and doctrine of ${country}.
5. EXECUTIVE SUMMARY: A high-level analysis of the overall legal health of the document.

APPLICABLE SPECIFIC LEGAL CONTEXT:
${countryContext}

GOLDEN RULES:
- DO NOT INVENT: If you are unsure of a specific law in ${country}, cite the applicable general principle of law.
- BE SPECIFIC: Don't say "the clause is vague", say "clause X is vague because it doesn't define term Y, which creates risk Z".
- INTELLIGENCE: Go beyond the obvious. Look for hidden risks in the fine print and in the interaction between different clauses.

RESPONSE FORMAT:
You must respond ONLY with a valid JSON object (no markdown, no backticks) with the following structure:
{
  "overall_risk": "low" | "medium" | "high",
  "risks": [
    {
      "severity": "low" | "medium" | "high" | "critical",
      "description": "Technical explanation of the risk",
      "location": "Exact reference to the clause/section",
      "recommendation": "Immediate corrective action"
    }
  ],
  "omissions": [
    {
      "description": "Missing element and why it is necessary",
      "recommendation": "Suggested text to include",
      "importance": "low" | "medium" | "high"
    }
  ],
  "sensitive_clauses": [
    {
      "clause": "Original text",
      "concern": "Explanation of legal sensitivity",
      "suggestion": "Suggested alternative wording",
      "severity": "low" | "medium" | "high"
    }
  ],
  "recommendations": ["Strategic advice 1", "Strategic advice 2", ...],
  "summary": "Professional executive analysis (2-3 paragraphs)",
  "confidence_score": 0-100
}

IMPORTANT: The analysis must be of "Law Firm Partner" quality, not a simple text summary.`,
      user: `Perform a thorough analysis of the following document:

TYPE: ${documentType}
COUNTRY: ${country}
${documentName ? `FILE: ${documentName}` : ""}

CONTENT:
---
${content}
---

Generate the legal technical report in JSON format.`,
    },
  };

  const systemPrompt = prompts[language].system;
  const userPrompt = prompts[language].user;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS, // Mixtral-8x7b-32768 for best quality
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.3, // Lower temperature for more consistent analysis
      max_tokens: 4000,
      top_p: 0.95,
      response_format: { type: "json_object" },
    });

    const responseContent = completion.choices[0]?.message?.content;

    if (!responseContent) {
      throw new Error("No se recibió respuesta del modelo de IA");
    }

    // Parse the JSON response
    const analysis = parseAnalysisResponse(responseContent);

    // Validate the analysis structure
    try {
      validateAnalysisResult(analysis);
    } catch (validationError: any) {
      console.error("AI Response Validation Failed:", {
        error: validationError.message,
        analysis,
        rawResponse: responseContent,
      });
      throw validationError;
    }

    return analysis;
  } catch (error: any) {
    console.error("Error analyzing document with Groq:", error);

    // Handle Groq not initialized
    if (error.message?.includes("Groq not initialized")) {
      throw new Error(
        "El servicio de análisis de documentos no está configurado. Por favor, contacta al administrador del sistema."
      );
    }

    // Handle rate limits
    if (error?.status === 429) {
      throw new Error(
        "Se ha alcanzado el límite de análisis por minuto. Por favor, intenta de nuevo en unos momentos."
      );
    }

    // Handle authentication errors
    if (error?.status === 401 || error?.status === 403) {
      throw new Error(
        "Error de autenticación del servicio de análisis. Por favor, contacta al administrador."
      );
    }

    // Handle parsing errors
    if (error.message?.includes("JSON")) {
      throw new Error(
        "Error al procesar la respuesta del análisis. Por favor, intenta de nuevo."
      );
    }

    throw new Error(
      `Error al analizar el documento: ${error.message || "Error desconocido"}`
    );
  }
}

/**
 * Parse the AI response and extract JSON
 */
function parseAnalysisResponse(response: string): DocumentAnalysisResult {
  let cleanedResponse = response.trim();

  try {
    // Attempt 1: Standard markdown removal
    if (cleanedResponse.includes("```")) {
      cleanedResponse = cleanedResponse
        .replace(/^```json\s*/i, "")
        .replace(/^```\s*/, "")
        .replace(/```\s*$/, "");
    }

    // Attempt 2: If JSON.parse fails, try to find the first '{' and last '}'
    // This handles cases where the model adds text before or after the JSON block
    try {
      return JSON.parse(cleanedResponse);
    } catch (e) {
      const startIndex = cleanedResponse.indexOf("{");
      const endIndex = cleanedResponse.lastIndexOf("}");

      if (startIndex !== -1 && endIndex !== -1) {
        const potentialJson = cleanedResponse.substring(
          startIndex,
          endIndex + 1
        );
        return JSON.parse(potentialJson);
      }
      throw e; // Rethrow if we can't find a JSON block
    }
  } catch (error) {
    console.error("Critical: Failed to parse AI analysis response", {
      error,
      rawResponse: response,
      cleanedAttempt: cleanedResponse,
    });
    throw new Error(
      "No se pudo interpretar la respuesta del análisis. El formato generado por la IA no fue válido."
    );
  }
}

/**
 * Validate the analysis result structure
 */
function validateAnalysisResult(analysis: any): void {
  if (!analysis || typeof analysis !== "object") {
    throw new Error("Análisis inválido: no es un objeto");
  }

  // Check required fields
  const requiredFields = [
    "overall_risk",
    "risks",
    "omissions",
    "sensitive_clauses",
    "recommendations",
    "summary",
  ];

  for (const field of requiredFields) {
    if (!(field in analysis)) {
      throw new Error(`Análisis inválido: falta el campo '${field}'`);
    }
  }

  // Validate overall_risk
  if (!["low", "medium", "high"].includes(analysis.overall_risk)) {
    throw new Error(
      "Análisis inválido: overall_risk debe ser low, medium o high"
    );
  }

  // Validate arrays
  if (!Array.isArray(analysis.risks)) {
    throw new Error("Análisis inválido: risks debe ser un array");
  }
  if (!Array.isArray(analysis.omissions)) {
    throw new Error("Análisis inválido: omissions debe ser un array");
  }
  if (!Array.isArray(analysis.sensitive_clauses)) {
    throw new Error("Análisis inválido: sensitive_clauses debe ser un array");
  }
  if (!Array.isArray(analysis.recommendations)) {
    throw new Error("Análisis inválido: recommendations debe ser un array");
  }

  // Validate summary is a string
  if (typeof analysis.summary !== "string") {
    throw new Error("Análisis inválido: summary debe ser una cadena de texto");
  }
}

/**
 * Quick analysis for shorter documents (under 1000 words)
 * Uses faster model with shorter response
 */
export async function quickAnalyzeDocument(
  params: DocumentAnalysisParams
): Promise<DocumentAnalysisResult> {
  const { content, documentType, country } = params;
  const groq = getGroq();

  const systemPrompt = `Eres un revisor legal experto. Analiza rápidamente el documento y proporciona los hallazgos más importantes en formato JSON.

Responde SOLO con JSON (sin markdown):
{
  "overall_risk": "low|medium|high",
  "risks": [{"severity": "low|medium|high|critical", "description": "...", "location": "...", "recommendation": "..."}],
  "omissions": [{"description": "...", "recommendation": "...", "importance": "low|medium|high"}],
  "sensitive_clauses": [{"clause": "...", "concern": "...", "suggestion": "...", "severity": "low|medium|high"}],
  "recommendations": ["..."],
  "summary": "Resumen breve",
  "confidence_score": 80
}`;

  const userPrompt = `Analiza este ${documentType} de ${country}:\n\n${content}`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS, // Use best model for legal accuracy (no errors)
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.3,
      max_tokens: 2000,
    });

    const responseContent = completion.choices[0]?.message?.content;
    if (!responseContent) {
      throw new Error("No response from AI");
    }

    const analysis = parseAnalysisResponse(responseContent);
    validateAnalysisResult(analysis);

    return analysis;
  } catch (error: any) {
    console.error("Error in quick analysis:", error);
    throw new Error(`Error en análisis rápido: ${error.message}`);
  }
}

/**
 * Apply review corrections to create an improved document
 * Uses analysis result (risks, omissions, recommendations) to produce corrected content
 */
export async function applyReviewCorrectionsToDocument(params: {
  originalContent: string;
  documentType: string;
  country: string;
  analysis: DocumentAnalysisResult;
}): Promise<string> {
  const { originalContent, documentType, country, analysis } = params;
  const groq = getGroq();

  const recommendationsText = [
    ...analysis.risks
      .filter((r) => r.recommendation)
      .map((r) => `• [${r.severity}] ${r.recommendation}`),
    ...analysis.omissions.map((o) => `• Omitido: ${o.recommendation}`),
    ...analysis.recommendations.map((r) => `• ${r}`),
  ].join("\n");

  const systemPrompt = `Eres un abogado experto en ${documentType} en la jurisdicción de ${country}. Tu tarea es aplicar las correcciones indicadas en el análisis legal al documento original.

REGLAS:
1. Mantén la estructura y formato del documento original.
2. Aplica todas las correcciones, sugerencias y recomendaciones del análisis.
3. Integra las cláusulas faltantes en los lugares apropiados.
4. Corrige los riesgos identificados con las redacciones sugeridas.
5. No agregues comentarios ni explicaciones extra.
6. Devuelve ÚNICAMENTE el documento corregido, listo para usar.`;

  const userPrompt = `DOCUMENTO ORIGINAL:
---
${originalContent}
---

ANÁLISIS LEGAL - CORRECCIONES A APLICAR:
Resumen: ${analysis.summary}

Correcciones y recomendaciones:
${recommendationsText}

Genera el documento corregido aplicando todas las recomendaciones:`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.3,
      max_tokens: 16000,
    });

    const content = completion.choices[0]?.message?.content;
    if (!content) {
      throw new Error("No se pudo generar el documento corregido");
    }

    return stripGeneratedNotes(content);
  } catch (error: any) {
    console.error("Error applying review corrections:", error);
    throw new Error(
      error.message || "Error al aplicar las correcciones al documento"
    );
  }
}
